import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { API_URL } from "../config";

// Profile pictures cache + batch fetching for the employees table
export const useEmployeeProfilePics = ({ employees }) => {
  const [employeeProfilePics, setEmployeeProfilePics] = useState({});
  const [loadingPics, setLoadingPics] = useState(new Set());
  const picCacheRef = useRef(new Map());

  const fetchEmployeeProfilePic = async (infinityCode) => {
    try {
      if (picCacheRef.current.has(infinityCode)) {
        return picCacheRef.current.get(infinityCode);
      }

      if (loadingPics.has(infinityCode)) {
        return null;
      }

      setLoadingPics(prev => new Set([...prev, infinityCode]));

      const response = await apiFetch(`${API_URL}/employees/pic/${infinityCode}`, { method: "GET" });

      // Handle 404 - employee has no profile picture
      if (response.status === 404) {
        setLoadingPics(prev => {
          const newSet = new Set(prev);
          newSet.delete(infinityCode);
          return newSet;
        });
        return null;
      }

      if (!response.ok) {
        throw new Error(`Pic fetch failed: ${response.status}`);
      }

      const blob = await response.blob();
      if (blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);

        picCacheRef.current.set(infinityCode, url);

        setLoadingPics(prev => {
          const newSet = new Set(prev);
          newSet.delete(infinityCode);
          return newSet;
        });

        return url;
      }

      setLoadingPics(prev => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });

      return null;
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error(`❌ Error loading pic for ${infinityCode}:`, err.message);
      }

      setLoadingPics(prev => {
        const newSet = new Set(prev);
        newSet.delete(infinityCode);
        return newSet;
      });

      return null;
    }
  };

  // ✅ Batch fetch all profile pictures when employees load
  useEffect(() => {
    const fetchAllProfilePics = async () => {
      if (employees.length === 0) return;

      const missingPics = employees
        .filter(emp => !employeeProfilePics[emp.id] && !picCacheRef.current.has(emp.id))
        .map(emp => emp.id);

      if (missingPics.length === 0) return;

      console.log(`📸 Fetching ${missingPics.length} profile pictures...`);

      const BATCH_SIZE = 5;
      const newPics = {};

      for (let i = 0; i < missingPics.length; i += BATCH_SIZE) {
        const batch = missingPics.slice(i, i + BATCH_SIZE);

        const results = await Promise.all(
          batch.map(async (id) => {
            try {
              const url = await fetchEmployeeProfilePic(id);
              return { id, url };
            } catch (err) {
              // Only log non-404 errors (404 means no profile pic, which is normal)
              if (err.response?.status !== 404 && err.message?.includes('404') === false) {
                console.error(`❌ Failed to load pic for ${id}:`, err.message);
              }
              return { id, url: null };
            }
          })
        );

        results.forEach(({ id, url }) => {
          if (url) {
            newPics[id] = url;
          }
        });

        if (i + BATCH_SIZE < missingPics.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      if (Object.keys(newPics).length > 0) {
        setEmployeeProfilePics(prev => ({ ...prev, ...newPics }));
        console.log(`✅ Loaded ${Object.keys(newPics).length} profile pictures!`);
      }
    };

    fetchAllProfilePics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  useEffect(() => {
    return () => {
      Object.values(employeeProfilePics).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });

      picCacheRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });

      picCacheRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { employeeProfilePics, loadingPics, fetchEmployeeProfilePic };
};
